import { useEffect, useState } from 'react'
import { AlertCircle, Download, ListChecks, X } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import {
  erreurEnClair,
  libelleInvitation,
  listeDeControleCsv,
  nomDeLaCommunaute,
  nomDuFichierCsv,
  type ListeDeControle,
  type SemaineDuCentre,
} from '../../../lib/communication/communauteWhatsapp'

/**
 * La liste de contrôle de la référente : pour chaque groupe de la semaine, les
 * parents attendus, leurs téléphones, et s'ils ont reçu l'invitation. Le lien
 * de la communauté peut circuler : la liste sert à n'accepter dans un groupe
 * que les familles réellement inscrites. Elle se télécharge en CSV (Excel).
 */
export function ListeDeControleWhatsapp({ semaine, onClose }: { semaine: SemaineDuCentre; onClose: () => void }) {
  const [liste, setListe] = useState<ListeDeControle | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  useEffect(() => {
    let actif = true
    supabase
      .rpc('liste_de_controle_whatsapp', { p_center_id: semaine.center_id, p_semaine_du: semaine.semaine_du })
      .then(({ data, error }) => {
        if (!actif) return
        if (error) setErreur(erreurEnClair(error))
        else setListe(data as ListeDeControle)
      })
    return () => {
      actif = false
    }
  }, [semaine.center_id, semaine.semaine_du])

  const telecharger = () => {
    if (!liste) return
    const url = URL.createObjectURL(new Blob([listeDeControleCsv(liste)], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = nomDuFichierCsv(liste)
    a.click()
    URL.revokeObjectURL(url)
  }

  const total = liste ? new Set(liste.groupes.flatMap(g => g.familles.map(f => f.user_id))).size : 0

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] flex flex-col">
        <div className="flex items-start justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <ListChecks className="h-5 w-5 mr-2" />
              Liste de contrôle — {nomDeLaCommunaute(semaine)}
            </h3>
            <p className="text-sm text-gray-600">
              Les parents attendus dans chaque groupe : n'acceptez dans un groupe que les familles de cette liste.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" aria-label="Fermer">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-6">
          {erreur && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800 flex items-start">
              <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>{erreur}</span>
            </div>
          )}
          {!liste && !erreur && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          )}
          {liste && liste.groupes.length === 0 && (
            <p className="text-sm text-gray-600">Aucune famille attendue cette semaine dans ce centre.</p>
          )}
          {liste?.groupes.map(g => (
            <section key={g.groupe}>
              <h4 className="font-semibold text-gray-900 mb-2">
                {g.groupe || 'Groupe sans nom'} <span className="text-sm font-normal text-gray-500">— {g.familles.length} famille(s)</span>
              </h4>
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Parent</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Téléphone(s)</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Enfant(s)</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Email</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Invitation</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {g.familles.map(f => (
                      <tr key={f.user_id}>
                        <td className="px-3 py-2 text-gray-900">{`${f.parent_nom} ${f.parent_prenom}`.trim() || '—'}</td>
                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                          {[f.telephone, f.telephone2].filter(Boolean).join(' · ') || '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{f.enfants}</td>
                        <td className="px-3 py-2 text-gray-700">{f.email || <span className="text-orange-700">sans adresse</span>}</td>
                        <td className={`px-3 py-2 ${f.invitation === 'envoye' ? 'text-green-700' : 'text-gray-500'}`}>{libelleInvitation(f)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-t border-gray-200">
          <span className="text-sm text-gray-600">{liste ? `${total} famille(s) attendue(s)` : ''}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">
              Fermer
            </button>
            <button
              onClick={telecharger}
              disabled={!liste || liste.groupes.length === 0}
              className="flex items-center space-x-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              <span>Télécharger (Excel)</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
